import React, { useState } from "react";
import Content from "../../../../ExtraComponent/Content";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { addChartingStrategyTag } from "../../../CommonAPI/Admin";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

/** ---------- Validation Schema ---------- **/
const validationSchema = Yup.object().shape({
  Strategytag: Yup.string()
    .trim()
    .required("Strategy Tag is required")
    .min(3, "Must be at least 3 characters"),
  Indicatorname: Yup.string()
    .trim()
    .required("Indicator Name is required")
    .min(3, "Must be at least 3 characters"),
  Description: Yup.string()
    .trim()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters long"),
  Image: Yup.mixed()
    .required("Image is required")
    .test("fileType", "Unsupported file type", (value) => {
      return value && ["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(value.type);
    })
    .test("fileSize", "File too large (max 2MB)", (value) => {
      return value && value.size <= 2 * 1024 * 1024; // 2MB limit
    }),
});

const StrategyTag = () => {
  const [imagePreview, setImagePreview] = useState("");
  const navigate = useNavigate();

  const initialValues = {
    Strategytag: "",
    Indicatorname: "",
    Description: "",
    Image: "", // File object
  };

  /** ---------- Image Upload Handler ---------- **/
  const handleImageUpload = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setFieldValue("Image", file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  /** ---------- Submit Handler ---------- **/
  const handleSubmit = async (values, { resetForm }) => {
    try {
      const res = await addChartingStrategyTag(values);

      if (res?.Status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Strategy added successfully!",
        });
        resetForm();
        setImagePreview("");
        navigate("/admin/Strategy-tag");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.message || "Failed to add strategy. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred. Please try again later.",
      });
    }
  };

  /** ---------- Render ---------- **/
  return (
    <Content Page_title="📄 Strategy Tag" button_status={false} backbutton_status={true}>
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item active " aria-current="page">  <IconButton onClick={() => navigate("/admin/Strategy-tag")} sx={{ color: "#333" }}>
            <ArrowBackIcon />
          
          </IconButton>Add New Strategy Tag</li>
        </ol>
      </nav>
      <div className="container ">
     

        <div className="card-bg-color p-4 shadow-sm" >
         

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue }) => (
              <Form>
                <div className="row g-4">
                  {/* Strategy Tag */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Strategy Tag</label>
                    <Field
                      type="text"
                      name="Strategytag"
                      placeholder="Enter Strategy Tag"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="Strategytag"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>

                  {/* Indicator Name */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Indicator Name</label>
                    <Field
                      type="text"
                      name="Indicatorname"
                      placeholder="Enter Indicator Name"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="Indicatorname"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>

                  {/* Description */}
                  <div className="col-12">
                    <label className="form-label fw-semibold">Description</label>
                    <Field
                      as="textarea"
                      name="Description"
                      placeholder="Enter detailed description..."
                      className="form-control"
                      rows="4"
                    />
                    <ErrorMessage
                      name="Description"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>

                  {/* Image Upload */}
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">Upload Image</label>
                    <input
                      type="file"
                      name="Image"
                      accept="image/*"
                      className="form-control"
                      onChange={(e) => handleImageUpload(e, setFieldValue)}
                    />
                    <div className="small text-muted mt-1">
                      Allowed formats: JPG, JPEG, PNG, GIF (max size: 2MB)
                    </div>
                    <ErrorMessage
                      name="Image"
                      component="div"
                      className="text-danger small mt-1"
                    />

                    {imagePreview && (
                      <div
                        style={{
                          marginTop: "12px",
                          border: "1px solid #eee",
                          borderRadius: "8px",
                          overflow: "hidden",
                          width: "120px",
                          height: "120px",
                        }}
                      >
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-lg-end mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary px-5 py-2"
                    style={{
                      borderRadius: "8px",
                      fontWeight: "500",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Submit
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Content>
  );
};

export default StrategyTag;
